import {
  Backdrop,
  Box,
  Button,
  ButtonProps,
  MenuItem,
  Menu as MuiMenu,
  SvgIcon,
  SvgIconTypeMap,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FC, MouseEvent, useState } from 'react';
import IconButton, { IconButtonProps } from '../IconButton';
import { Row } from '../Layout';

const MenuList = styled(MuiMenu)({
  margin: 0,
  padding: 0,
  zIndex: 2010,
});

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
  fontSize: theme.fontSizes.sm,
  lineHeight: '16px',
  fontWeight: theme.typography.fontWeightMedium,
}));

const ItemIcon = styled(SvgIcon)(({ theme }) => ({
  fontSize: theme.fontSizes.lg,
}));

const RequiresActionBadge = styled(Badge)(({ theme }) => ({
  marginLeft: theme.spacing(theme.spacingSizes.xxs),
}));

const TransparentBackdrop = styled(Backdrop)({
  backgroundColor: 'transparent',
});

type Props = {
  handleIcon?: OverridableComponent<SvgIconTypeMap<object>>;
  handleVariant?: IconButtonProps['variant'];
  buttonVariant?: ButtonProps['variant'];
  handleSize?: 'small' | 'medium';
  className?: string;
  items: Array<{
    label: string;
    icon: OverridableComponent<SvgIconTypeMap<object>>;
    onClick: () => void;
    disabled?: boolean;
    tooltip?: string;
    requiresAction?: boolean;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  }>;
  alignWithIconRightEdge?: boolean;
  label?: string;
};

const Menu: FC<Props> = ({
  handleIcon: Handle,
  items,
  className,
  handleSize = 'medium',
  handleVariant = 'default',
  buttonVariant = 'outlined',
  alignWithIconRightEdge = false,
  label,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!items.length) return null;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  }) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleButton = label ? (
    <Button
      onClick={handleClick}
      variant={buttonVariant}
      size="large"
      endIcon={Handle ? <Handle fontSize="small" /> : undefined}
    >
      {label}
    </Button>
  ) : Handle ? (
    <IconButton variant={handleVariant} onClick={handleClick} size={handleSize}>
      <Handle fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Box className={className}>
      {handleButton}
      <MenuList
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slots={{
          backdrop: TransparentBackdrop,
        }}
        {...(alignWithIconRightEdge
          ? {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            }
          : {})}
      >
        {items.map(
          ({
            label,
            icon,
            onClick,
            disabled,
            tooltip,
            requiresAction,
            color,
          }) => (
            <MenuItem
              key={label}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (disabled) {
                  return;
                }
                handleClose();
                onClick();
              }}
              style={{
                pointerEvents: disabled && tooltip ? 'auto' : undefined,
              }}
            >
              <Row centered spacing="xs">
                <ItemIcon as={icon} fontSize="small" color={color} />
                {tooltip ? (
                  <Tooltip title={tooltip}>
                    <Label variant="body2">{label}</Label>
                  </Tooltip>
                ) : (
                  <Label variant="body2">{label}</Label>
                )}
                {requiresAction ? (
                  <RequiresActionBadge variant="dot" color="warning" />
                ) : null}
              </Row>
            </MenuItem>
          ),
        )}
      </MenuList>
    </Box>
  );
};

export default Menu;
